import { z } from 'zod';
import { financeDateOnlySchema, financeMoneyCentsSchema, financeQuantityTenThousandthsSchema, financeUnitPriceCentsSchema, } from './finance-invoice.contract.js';
export const financePortalInvoicesCapability = 'finance.portal-invoices.v1';
export const financePortalInvoiceScopeProviderId = 'finance.server.portal-invoice-scope';
export const financePortalInvoiceResourceType = 'finance.invoice';
export const financePortalInvoicesReadGrant = 'finance.portal-invoices.read';
export const financePortalInvoiceStatusSchema = z.enum(['sent', 'partial', 'paid']);
export const financePortalInvoiceLineSchema = z.strictObject({
    position: z.number().int().min(1).max(100),
    description: z.string().trim().min(1).max(500),
    quantityTenThousandths: financeQuantityTenThousandthsSchema,
    unitLabel: z.string().trim().min(1).max(20),
    unitPriceCents: financeUnitPriceCentsSchema,
    lineTotalCents: financeMoneyCentsSchema,
}).superRefine((line, context) => {
    const expected = (BigInt(line.quantityTenThousandths) * BigInt(line.unitPriceCents) + 5000n) / 10000n;
    if (BigInt(line.lineTotalCents) !== expected) {
        context.addIssue({
            code: 'custom', path: ['lineTotalCents'],
            message: 'Portal invoice line total does not reconcile.',
        });
    }
});
const financePortalAggregateCentsSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
export const financePortalInvoiceSchema = z.strictObject({
    id: z.string().uuid(),
    invoiceNumber: z.string().trim().min(1).max(40),
    status: financePortalInvoiceStatusSchema,
    currencyCode: z.literal('USD'),
    invoiceDate: financeDateOnlySchema,
    dueDate: financeDateOnlySchema,
    memo: z.string().max(2_000).nullable(),
    subtotalCents: financeMoneyCentsSchema,
    discountCents: financeMoneyCentsSchema,
    taxCents: financeMoneyCentsSchema,
    totalCents: financeMoneyCentsSchema,
    amountPaidCents: financeMoneyCentsSchema,
    amountDueCents: financeMoneyCentsSchema,
    isOverdue: z.boolean(),
    lines: z.array(financePortalInvoiceLineSchema).min(1).max(100),
}).superRefine((invoice, context) => {
    const lineSubtotal = invoice.lines.reduce((sum, line) => sum + BigInt(line.lineTotalCents), 0n);
    if (lineSubtotal !== BigInt(invoice.subtotalCents)) {
        context.addIssue({
            code: 'custom', path: ['subtotalCents'],
            message: 'Portal invoice lines do not reconcile to the subtotal.',
        });
    }
    if (invoice.totalCents !== invoice.subtotalCents - invoice.discountCents + invoice.taxCents) {
        context.addIssue({
            code: 'custom', path: ['totalCents'],
            message: 'Portal invoice total does not reconcile.',
        });
    }
    if (invoice.amountPaidCents > invoice.totalCents ||
        invoice.amountDueCents > invoice.totalCents - invoice.amountPaidCents) {
        context.addIssue({
            code: 'custom', path: ['amountDueCents'],
            message: 'Portal invoice paid and due amounts exceed its total.',
        });
    }
    if (invoice.status === 'paid' && invoice.amountDueCents !== 0) {
        context.addIssue({
            code: 'custom', path: ['status'],
            message: 'A paid Portal invoice cannot retain an amount due.',
        });
    }
});
export const financePortalInvoicesResponseSchema = z.strictObject({
    contractVersion: z.literal(financePortalInvoicesCapability),
    data: z.array(financePortalInvoiceSchema).max(100),
    summary: z.strictObject({
        invoiceCount: z.number().int().min(0).max(100),
        totalBilledCents: financePortalAggregateCentsSchema,
        totalPaidCents: financePortalAggregateCentsSchema,
        totalDueCents: financePortalAggregateCentsSchema,
        overdueCount: z.number().int().min(0).max(100),
    }),
}).superRefine((response, context) => {
    const totals = response.data.reduce((summary, invoice) => ({
        invoiceCount: summary.invoiceCount + 1,
        totalBilledCents: summary.totalBilledCents + invoice.totalCents,
        totalPaidCents: summary.totalPaidCents + invoice.amountPaidCents,
        totalDueCents: summary.totalDueCents + invoice.amountDueCents,
        overdueCount: summary.overdueCount + Number(invoice.isOverdue),
    }), {
        invoiceCount: 0,
        totalBilledCents: 0,
        totalPaidCents: 0,
        totalDueCents: 0,
        overdueCount: 0,
    });
    for (const [key, value] of Object.entries(totals)) {
        if (response.summary[key] !== value) {
            context.addIssue({
                code: 'custom', path: ['summary', key],
                message: 'Portal invoice summary does not reconcile to its rows.',
            });
        }
    }
});
//# sourceMappingURL=finance-portal-invoice.contract.js.map